import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { api } from './api';

const AuthContext = createContext({ user: null, loading: true, login: () => {}, logout: () => {}, refresh: () => {} });

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem('alb_token');
        if (!token) { setUser(null); setLoading(false); return; }
        try {
            const r = await api.get('/auth/me');
            setUser(r.data);
        } catch (error) {
            // Only treat the session as invalid when the server explicitly rejects
            // the token. A network hiccup or a transient 5xx (e.g. right after
            // verifying the birthdate/age, which triggers an extra refresh) must
            // not log the user out, or the wallet/views will look like they
            // vanished when in fact the account is untouched.
            const statusCode = error?.response?.status;
            if (statusCode === 401 || statusCode === 403) {
                localStorage.removeItem('alb_token');
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback((token, userObj) => {
        localStorage.setItem('alb_token', token);
        setUser(userObj);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('alb_token');
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
